import { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { SignupForm } from '../../../components/auth/signup/SignupForm'
import type { SignupData } from '../../../components/auth/signup/SignupForm'

import type { SexCode } from '@/api/model/userType'
import { updateUserAgreementApi, updateUserInfoApi } from '@/api/userApi'
import { checkTokensOrTryRefresh } from '@/utils/auth'
import { getUserIdFromToken } from '@/utils/jwt'
import { useInvalidateAuth } from '@/hooks/queries/auth'
import { useUserStore } from '@/store/userStore'
import { useAuthStore } from '@/store/authStore'
import { logger } from '@/utils/logger'
import { backupTokensToNative } from '@/utils/nativeAuth'

export default function SignupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const privacyConsent = searchParams.get('privacyConsent') === 'true'
  // 토큰으로 user_id 하는 것이 더 안전함

  const user_id = getUserIdFromToken(useAuthStore.getState().getAccessToken() || '')
  const invalidateAuth = useInvalidateAuth()
  const { userInfo, setUserInfo } = useUserStore()

  useEffect(() => {
    ;(async () => {
      await checkTokensOrTryRefresh()
    })()
  }, [])

  const handleSignupComplete = useCallback(
    async (data: SignupData) => {
      const _body = {
        nickname: data.nickname,
        sex_code: data.gender === '남성' ? 'male' : data.gender === '여성' ? 'female' : ('other' as SexCode),
        birthday: data.birthDate.split('-').join(''),
        grade_code: data.grade,
        school_code: data.schoolCode || null,
        school_name: data.schoolName || null,
        mbti: data.mbti || null,
        type_id: data.vltType 
      }
      try {
        logger.auth(`회원가입 정보 업데이트 시작 - User ID: ${user_id}`)
        await updateUserInfoApi(Number(user_id), _body)
        logger.success('회원가입 정보 업데이트 성공')

        try {
          logger.auth('약관 동의 업데이트 중...')
          await updateUserAgreementApi(Number(user_id), {
            is_agree: privacyConsent
          })
          logger.success('약관 동의 완료')

          // 업데이트할 사용자 정보
          const updatedUserInfo = {
            ...userInfo,
            user_id: Number(user_id),
            nickname: data.nickname,
            sex_code: _body.sex_code,
            birthday: _body.birthday,
            grade_code: data.grade,
            school_name: data.schoolName,
            school_code: data.schoolCode,
            mbti: data.mbti,
            type_id: data.vltType,
            is_agree: privacyConsent
          }

          // userStore 업데이트 (localStorage)
          setUserInfo(updatedUserInfo)
          logger.storage('사용자 정보 스토어 업데이트 완료')

          // authStore도 함께 업데이트 (메모리)
          useAuthStore.getState().setUserInfo({
            user_id: Number(user_id),
            nickname: data.nickname,
            email: userInfo.email || null,
            is_agree: privacyConsent,
            provider: userInfo.join_path || 'kakao'
          })
          logger.auth('인증 스토어 업데이트 완료')

          // ✅ Keychain 업데이트 (회원가입 완료 시 토큰 백업)
          const accessToken = useAuthStore.getState().getAccessToken()
          const refreshToken = useAuthStore.getState().getRefreshToken()

          if (accessToken && refreshToken) {
            backupTokensToNative(accessToken, refreshToken, {
              id: Number(user_id),
              name: data.nickname,
              email: userInfo.email || null,
              is_agree: privacyConsent,
              provider: userInfo.join_path || 'kakao'
            })
            logger.success('✅ Keychain 업데이트 완료 (회원가입)')
          } else {
            logger.warn('⚠️ 토큰이 없어서 Keychain 업데이트를 건너뜁니다')
          }

          // 인증 캐시 무효화
          invalidateAuth()

          // 서버의 데이터 업데이트가 완료될 시간을 주기 위해 약간의 지연 추가
          logger.success('회원가입 완료 - 홈으로 이동')
          setTimeout(() => {
            navigate('/')
          }, 1000)
        } catch (err) {
          logger.error('약관 동의 중 오류 발생:', err)
        }
      } catch (err) {
        logger.error('회원가입 정보 업데이트 중 오류 발생:', err)
      }
    },
    [navigate, privacyConsent, user_id, invalidateAuth, userInfo, setUserInfo]
  )

  const initialSignupData = {
    privacyConsent,
    user_id: user_id ? String(user_id) : ''
  }

  return (
    <div className='flex flex-col min-h-screen relative font-sans noto-sans-kr'>
      <SignupForm onComplete={handleSignupComplete} initialSignupData={initialSignupData} />
    </div>
  )
}
